<template>
	<el-main class="layout-draw-page">
		<div class="draw-page-inner">
			<!-- 顶部标题 -->
			<div class="draw-title">
				<h2>AI 图像生成</h2>
				<p>选择模型，输入描述，即刻生成专属图像</p>
			</div>

			<!-- 紧凑 3 步指引条 -->
			<div class="draw-howto">
				<span class="draw-howto__step">
					<span class="draw-howto__num">1</span>
					<i class="el-icon-collection-tag"></i>
					选择场景或上传参考图
				</span>
				<i class="draw-howto__sep el-icon-arrow-right"></i>
				<span class="draw-howto__step">
					<span class="draw-howto__num">2</span>
					<i class="el-icon-edit"></i>
					输入或修改描述文字
				</span>
				<i class="draw-howto__sep el-icon-arrow-right"></i>
				<span class="draw-howto__step">
					<span class="draw-howto__num draw-howto__num--accent">3</span>
					<i class="el-icon-magic-stick"></i>
					点击右下角生成图像
				</span>
			</div>

			<!-- 左右两栏布局：左场景侧栏 + 右主工作区。整块工作区紧贴顶部展示 -->
			<div class="draw-grid">
				<!-- 左：场景侧栏 -->
				<layout-draw-scenes
					class="draw-grid__aside"
					:_active-guid="activeSceneGuid"
					@select="onSceneSelect"
					@clear="onSceneClear"
				></layout-draw-scenes>

				<!-- 右：主工作区（输入 + 结果 + 历史） -->
				<div class="draw-grid__main">
					<layout-draw-input
						ref="inputRef"
						:_status="status"
						:_wsReady="wsReady"
						@generate="onGenerate"
						@scene-cleared="onInputSceneCleared"
					></layout-draw-input>

					<layout-draw-result
						:_images="genImages"
						:_status="status"
						:_referenceImage="genReferenceImage"
						:_prompt="genPrompt"
					></layout-draw-result>

					<layout-draw-history ref="historyRef"></layout-draw-history>
				</div>
			</div>
		</div>
	</el-main>
</template>

<script>
import LayoutDrawInput from '@/views/app/layout-draw/layout-draw-input.vue';
import LayoutDrawResult from '@/views/app/layout-draw/layout-draw-result.vue';
import LayoutDrawHistory from '@/views/app/layout-draw/layout-draw-history.vue';
import LayoutDrawScenes from '@/views/app/layout-draw/layout-draw-scenes.vue';
import { getToken } from '@/utils/auth';
import config from '@/apis/config';

export default {
	name: 'layout-draw',
	components: { LayoutDrawInput, LayoutDrawResult, LayoutDrawHistory, LayoutDrawScenes },

	data() {
		return {
			genImages: [],
			genReferenceImage: '', // 本次生成所用参考图（图生图时透传给结果弹窗展示）
			genPrompt: '',         // 本次生成所用提示词（用于结果弹窗展示）
			status: '0',         // 0-待生成 1-生成中 2-已完成
			ws: null,
			wsReady: false,
			wsReconnectTimer: null,
			pendingOrderNo: null, // 断线时记录正在生成的订单号，重连后用于恢复
			activeSceneGuid: '',  // 当前选中场景 guid（与左栏 + 输入卡片同步）
		};
	},

	created() {
		this.connectWs();
	},

	beforeDestroy() {
		// 离开绘画页时主动关闭，不触发重连
		if (this.wsReconnectTimer) clearTimeout(this.wsReconnectTimer);
		if (this.ws) {
			this.ws.onclose = null; // 取消重连回调
			this.ws.close();
		}
	},

	methods: {
		// ── WebSocket 连接管理 ─────────────────────────────────────────
		connectWs() {
			if (this.ws && (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING)) return;

			this.ws = new WebSocket(config.wsUrl);

			this.ws.onopen = () => {
				this.wsReady = true;
				// 重连后若有未完成的绘画任务，发 resume_draw 恢复结果
				if (this.pendingOrderNo) {
					this.ws.send(JSON.stringify({ type: 'resume_draw', orderNo: this.pendingOrderNo }));
				}
			};

			this.ws.onmessage = (e) => {
				try {
					const event = JSON.parse(e.data);
					this.handleWsEvent(event);
				} catch { /* ignore malformed */ }
			};

			this.ws.onclose = () => {
				this.wsReady = false;
				// 生成中断线：保持生成中状态，等重连后自动恢复（有 pendingOrderNo 时）
				// 若没有 pendingOrderNo（未收到 image_generating），才提示重试
				if (this.status === '1' && !this.pendingOrderNo) {
					this.status = '0';
					this.$message({ message: '连接中断，请重新生成', type: 'warning' });
				}
				this.wsReconnectTimer = setTimeout(() => this.connectWs(), 3000);
			};

			this.ws.onerror = () => {
				this.wsReady = false;
			};
		},

		// ── 处理服务端推送的绘画事件 ──────────────────────────────────
		handleWsEvent(event) {
			switch (event.type) {
				case 'image_generating':
					// 订单已创建，记录 orderNo 用于断线恢复
					this.pendingOrderNo = event.orderNo || null;
					break;

				case 'image_ready':
					this.pendingOrderNo = null;
					this.genImages = event.urls || [];
					this.status = '2';
					this.$refs.historyRef && this.$refs.historyRef.reload();
					break;

				case 'image_error':
					this.pendingOrderNo = null;
					this.$message({ message: event.message || '图像生成失败', type: 'error' });
					this.status = '0';
					break;

				case 'draw_resume_not_found':
					// 服务端没有此订单的缓冲（超时或重启），让用户重试
					this.pendingOrderNo = null;
					this.status = '0';
					this.$message({ message: '生成结果已过期，请重新生成', type: 'warning' });
					break;
			}
		},

		// ── 场景事件 ────────────────────────────────────────────────
		// 左栏选了场景 → 调用 input 的 applyScene + 同步 activeSceneGuid
		onSceneSelect(scene) {
			this.activeSceneGuid = scene.guid;
			this.$refs.inputRef && this.$refs.inputRef.applyScene(scene);
		},

		// 左栏清除场景 / 选了"自定义创作" → 同步清掉 input 的场景标记
		onSceneClear() {
			this.activeSceneGuid = '';
			this.$refs.inputRef && this.$refs.inputRef.clearScene();
		},

		// 输入卡片内部触发的清除（如用户切了模型）→ 同步左栏选中态
		onInputSceneCleared() {
			this.activeSceneGuid = '';
		},

		// ── 子组件事件 ────────────────────────────────────────────────
		// layout-draw-input 触发：发起绘画请求
		onGenerate(params) {
			if (!this.wsReady) {
				this.$message({ message: 'WebSocket 未连接，正在重连...', type: 'warning' });
				this.connectWs();
				return;
			}

			this.status = '1';
			this.genImages = [];
			this.genReferenceImage = params.referenceImage || '';
			this.genPrompt = params.prompt || '';

			this.ws.send(JSON.stringify({
				type:           'generate_image',
				token:          getToken(),
				app_type:       'xiaoyipc',
				app_guid:       this.$store.state.merchantGuid || '',
				modelGuid:      params.modelGuid,
				prompt:         params.prompt,
				negativePrompt: params.negativePrompt,
				width:          params.width,
				height:         params.height,
				// 参考图（图生图）：Node WS 透传给 PHP inner.draw/start，
				// 再回到 Node DrawLoop 调 OpenRouter 时构造 multimodal content
				referenceImage: params.referenceImage || '',
				// 来源场景 guid：仅追溯统计用
				sceneGuid:      params.sceneGuid || '',
			}));
		},
	},
};
</script>

<style lang="scss">
.layout-draw-page {
	padding: 0 !important;
	background: linear-gradient(180deg, #f7faff 0%, #f4f5f7 100%);
	min-height: calc(100vh - 60px);
	overflow-y: auto;

	.draw-page-inner {
		max-width: 1280px;
		margin: 0 auto;
		// 顶部 padding 压到 16px，让整个工作区往上贴，跟标题/指引条之间不要那么空
		padding: 16px 32px 60px;
	}

	// 左右两栏：280 场景侧栏 + 1fr 主区（与原来一致）
	.draw-grid {
		display: grid;
		grid-template-columns: 280px 1fr;
		gap: 20px;
		align-items: start;
	}

	.draw-grid__aside {
		position: sticky;
		top: 12px;
		// 不拉伸，让它自然根据内容高度展示，并 sticky 跟随滚动
	}

	.draw-grid__main {
		min-width: 0;
		display: flex;
		flex-direction: column;
		gap: 18px;
	}

	// 窄屏：场景区折叠到上方（堆叠）
	@media (max-width: 920px) {
		.draw-grid {
			grid-template-columns: 1fr;
		}
		.draw-grid__aside {
			position: static;
			max-height: 320px;
		}
	}

	.draw-title {
		text-align: center;
		margin-bottom: 10px; // 18 → 10，缩小标题和指引条之间空隙

		h2 {
			font-size: 20px;   // 24 → 20，标题瘦身
			font-weight: 700;
			color: #1a1a2e;
			margin: 0 0 4px;
		}

		p {
			font-size: 12px;
			color: #999;
			margin: 0;
		}
	}

	// 紧凑 3 步指引条
	.draw-howto {
		display: inline-flex;
		align-items: center;
		gap: 14px;
		padding: 6px 18px;          // 10 22 → 6 18，胶囊变扁
		margin: 0 auto 12px;        // 16 → 12，进一步靠近工作区
		background: rgba(255, 255, 255, 0.7);
		backdrop-filter: blur(10px);
		border: 1px solid rgba(106, 92, 246, 0.12);
		border-radius: 999px;
		box-shadow: 0 4px 16px rgba(106, 92, 246, 0.08);
		font-size: 13px;
		color: #555;
		max-width: 100%;
		flex-wrap: wrap;
		justify-content: center;
		// 让自己居中
		margin-left: auto;
		margin-right: auto;
		display: flex;
		width: fit-content;

		&__step {
			display: inline-flex;
			align-items: center;
			gap: 5px;
			white-space: nowrap;

			i:not(.draw-howto__sep):not(.draw-howto__num) {
				font-size: 13px;
				color: #6a5cf6;
			}
		}

		&__num {
			width: 18px;
			height: 18px;
			border-radius: 50%;
			background: #ece6ff;
			color: #6a5cf6;
			font-size: 11px;
			font-weight: 700;
			display: inline-flex;
			align-items: center;
			justify-content: center;
			flex-shrink: 0;

			&--accent {
				background: linear-gradient(135deg, #2c8df4, #6a5cf6);
				color: #fff;
				box-shadow: 0 2px 6px rgba(106, 92, 246, 0.35);
			}
		}

		&__sep {
			font-size: 11px;
			color: #c4cdd7;
		}
	}
}
</style>
